import { Ref } from 'vue';
import { Annotation, LikesMap, LikesFlagMap } from '../types/annotation.types';
import { xhr } from '@/xhr/endpoints';

export function useAnnotationLikes(
  annotation: Ref<Annotation>,
  annotationPublicLikesTotalsMap: Ref<LikesMap>,
  annotationPublicLikesFlag: Ref<LikesFlagMap>,
  visitor: Ref<string>,
  setAnnotationPublicLikesTotalsMap: (map: LikesMap) => void,
  setAnnotationPublicLikesFlag: (flag: LikesFlagMap) => void
) {
  const doRemoveLike = async (id: string | undefined, status: number): Promise<boolean> => {
    if (id === undefined) return false;

    const updatedLikesFlag: LikesFlagMap = {
      ...annotationPublicLikesFlag.value,
      [annotation.value._id]: null,
    };

    let updatedLikes: LikesMap = {
      ...annotationPublicLikesTotalsMap.value,
    };

    if (status === 1) {
      updatedLikes = {
        ...updatedLikes,
        [annotation.value._id]: {
          ...updatedLikes[annotation.value._id],
          likes: (annotationPublicLikesTotalsMap.value[annotation.value._id]?.likes || 0) - 1,
        },
      };
    } else if (status === 0) {
      updatedLikes = {
        ...updatedLikes,
        [annotation.value._id]: {
          ...updatedLikes[annotation.value._id],
          dislikes: (annotationPublicLikesTotalsMap.value[annotation.value._id]?.dislikes || 0) - 1,
        },
      };
    }

    setAnnotationPublicLikesFlag(updatedLikesFlag);
    setAnnotationPublicLikesTotalsMap(updatedLikes);

    try {
      await xhr.deleteAnnotationLike(id);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const processLike = async (likeValue: number, annId: string) => {
    const updatedLikesFirst: LikesMap = {
      ...annotationPublicLikesTotalsMap.value,
    };

    if (likeValue === 1) {
      if (annotationPublicLikesFlag.value[annotation.value._id]?.status === 'dislike') {
        updatedLikesFirst[annotation.value._id] = {
          ...updatedLikesFirst[annotation.value._id],
          dislikes: (updatedLikesFirst[annotation.value._id]?.dislikes || 0) - 1,
        };
      }
      updatedLikesFirst[annotation.value._id] = {
        ...updatedLikesFirst[annotation.value._id],
        likes: (updatedLikesFirst[annotation.value._id]?.likes || 0) + 1,
      };
    } else if (likeValue === 0) {
      if (annotationPublicLikesFlag.value[annotation.value._id]?.status === 'like') {
        updatedLikesFirst[annotation.value._id] = {
          ...updatedLikesFirst[annotation.value._id],
          likes: (updatedLikesFirst[annotation.value._id]?.likes || 0) - 1,
        };
      }
      updatedLikesFirst[annotation.value._id] = {
        ...updatedLikesFirst[annotation.value._id],
        dislikes: (updatedLikesFirst[annotation.value._id]?.dislikes || 0) + 1,
      };
    }

    const updatedLikesFlagFirst: LikesFlagMap = {
      ...annotationPublicLikesFlag.value,
      [annotation.value._id]: {
        status: likeValue === 1 ? 'like' : 'dislike',
      },
    };

    setAnnotationPublicLikesFlag(updatedLikesFlagFirst);
    setAnnotationPublicLikesTotalsMap(updatedLikesFirst);

    const data = {
      visitor_id: visitor.value,
      debate_round_annotation_id: `${annId}`,
      status: likeValue,
    };

    try {
      const likeRes = await xhr.addAnnotationLike(data);

      const updatedLikesFlag: LikesFlagMap = {
        ...annotationPublicLikesFlag.value,
        [likeRes.debate_round_annotation_id]: {
          status: likeRes.status,
          id: likeRes.id,
        },
      };

      setAnnotationPublicLikesFlag(updatedLikesFlag);
    } catch (error) {
      console.error('Error processing like:', error);
    }
  };

  const doLike = async () => {
    if (annotationPublicLikesFlag.value[annotation.value._id]?.status === 'like') {
      await doRemoveLike(annotationPublicLikesFlag.value[annotation.value._id]?.id, 1);
    } else {
      await processLike(1, annotation.value._id);
    }
  };

  const doDislike = async () => {
    if (annotationPublicLikesFlag.value[annotation.value._id]?.status === 'dislike') {
      await doRemoveLike(annotationPublicLikesFlag.value[annotation.value._id]?.id, 0);
    } else {
      await processLike(0, annotation.value._id);
    }
  };

  return {
    doLike,
    doDislike,
    doRemoveLike,
    processLike,
  };
}
